/**
 * Recognising "this partner has no business yet" from a GET /api/business/me failure.
 *
 * The backend answers that question with three different 404s, checked in order:
 *
 *   "No business membership found for this user"  <- every stranded owner
 *   "No business linked to this membership"       <- membership with no businessId
 *   "Business profile not found"                  <- dangling membership (rare)
 *
 * Dashboard.js used to match only the third, which is the one a new partner cannot
 * produce — so the rescue redirect fired for exactly the wrong people. That is the
 * bug behind #28/#29.
 *
 * `code` is preferred once the backend sends it; the phrase list is the fallback so
 * this keeps working against a backend that hasn't shipped the code yet.
 */

export const NO_BUSINESS_CODE = "NO_BUSINESS";

const NO_BUSINESS_PHRASES = [
  "no business membership found",
  "no business linked to this membership",
  "business profile not found",
];

/**
 * The message an api.js rejection carries, whatever shape it arrived in.
 *
 * api.js unwraps before throwing (`error.response?.data || { message: error.message }`),
 * so this normally receives the server body ({error, code}) or {message} on a network
 * failure. The response.* fallbacks cover a raw Axios error too, in case a future caller
 * forgets to unwrap — and a real server `.error` is checked before the generic
 * `.message` so the specific reason wins over "Request failed with status code 404".
 */
export const backendErrorMessage = (error) =>
  error?.error ||
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  "";

export const isNoBusinessError = (error) => {
  if (!error) return false;
  if (error.code === NO_BUSINESS_CODE) return true;

  const message = backendErrorMessage(error);
  if (typeof message !== "string") return false;

  const lower = message.toLowerCase();
  return NO_BUSINESS_PHRASES.some((phrase) => lower.includes(phrase));
};
